"use client";

import { type ReactNode, useEffect, useRef } from "react";

/**
 * Aparición suave al entrar en pantalla. Se anima una sola vez por elemento y después se
 * desconecta el observador — nada de listeners de scroll ni de recalcular en cada cuadro.
 *
 * Si `reveal-on` no está en el `<html>` (sin JavaScript en el arranque, sin `IntersectionObserver`,
 * o el sistema pide menos movimiento) el elemento ya se ve por CSS y esto no hace nada.
 */
export function Reveal({
  children,
  className,
  /** Retraso en ms, para escalonar los elementos de una grilla. */
  retraso = 0,
}: {
  children: ReactNode;
  className?: string;
  retraso?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!document.documentElement.classList.contains("reveal-on")) return;

    const observador = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          if (entrada.isIntersecting) {
            el.dataset.visible = "true";
            observador.disconnect();
          }
        }
      },
      // Se dispara un poco antes de que el elemento toque el borde inferior, para que llegue
      // animado y no "saltando" justo al aparecer.
      { rootMargin: "0px 0px -12% 0px", threshold: 0.05 },
    );

    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      data-reveal
      className={className}
      style={retraso ? { transitionDelay: `${retraso}ms` } : undefined}
    >
      {children}
    </div>
  );
}
